import { UpdateRolePermissionsRequest } from "@api/apiClient";
import { useRolePermissions, useRoles, useUpdateRolePermissions } from "@api/roles";
import i18n from "@i18n/i18n";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
  Stack,
  Typography,
} from "@mui/material";
import { observer } from "mobx-react";
import { useEffect, useState } from "react";

const RoleMappingGrid = observer(() => {
  const [selectedRoleId, setSelectedRoleId] = useState<string>("");
  const [selectedRoleName, setSelectedRoleName] = useState<string>("");
  const [selectedPermissions, setSelectedPermissions] = useState<string[]>([]);
  const [initialPermissions, setInitialPermissions] = useState<string[]>([]);
  const [availablePermissions, setAvailablePermissions] = useState<string[]>([]);
  const [defaultPermissions, setDefaultPermissions] = useState<string[]>([]);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");

  const getRoles = useRoles();
  const getRolePermissions = useRolePermissions(selectedRoleId);
  const updateRolePermissions = useUpdateRolePermissions(selectedRoleId, {
    onSuccess: () => {
      setSnackbarMessage(i18n.t("role-mapping-tab.notification.save-success"));
      setOpenSnackbar(true);

      // Refetch the role permissions to update the UI with saved data
      // Keep the dropdown selection intact
      getRolePermissions.refetch();
    },
  });

  // Update permissions when role permissions are loaded
  useEffect(() => {
    if (getRolePermissions.data) {
      // Available permissions (all from Permissions table) - options to show
      setAvailablePermissions(getRolePermissions.data.availablePermissions || []);

      // Selected permissions (default + user-saved) - pre-checked boxes
      setSelectedPermissions(getRolePermissions.data.selectedPermissions || []);
      setInitialPermissions(getRolePermissions.data.selectedPermissions || []);

      // Default permissions (non-editable) - to disable checkboxes
      setDefaultPermissions(getRolePermissions.data.defaultPermissions || []);
    }
  }, [getRolePermissions.data]);

  const handleRoleChange = (roleId: string) => {
    const role = getRoles.data?.roles?.find((r) => r.roleId === roleId);
    setSelectedRoleId(roleId);
    setSelectedRoleName(role?.name || "");
    setSelectedPermissions([]);
    setInitialPermissions([]);
    setAvailablePermissions([]);
    setDefaultPermissions([]);
  };

  const handlePermissionToggle = (permissionName: string) => {
    // Don't allow toggling default permissions
    if (defaultPermissions.includes(permissionName)) {
      return;
    }

    setSelectedPermissions((prev) => {
      const isCurrentlySelected = prev.includes(permissionName);

      // If toggling app.manage
      if (permissionName === "app.manage") {
        if (isCurrentlySelected) {
          // Unchecking app.manage - just remove it, keep default permissions
          return prev.filter((p) => p !== permissionName);
        } else {
          // Checking app.manage - uncheck all non-default permissions and add only app.manage
          // Keep only default permissions and add app.manage
          const defaultOnly = prev.filter((p) => defaultPermissions.includes(p));
          return [...defaultOnly, permissionName];
        }
      }

      // If app.manage is selected, don't allow toggling other permissions
      if (prev.includes("app.manage")) {
        return prev;
      }

      // Normal toggle for other permissions
      if (isCurrentlySelected) {
        return prev.filter((p) => p !== permissionName);
      } else {
        return [...prev, permissionName];
      }
    });
  };

  const handleSave = async () => {
    if (!selectedRoleId) return;

    const request = new UpdateRolePermissionsRequest({
      permissions: selectedPermissions,
    });

    await updateRolePermissions.mutateAsync(request);
  };

  const handleCancel = () => {
    setSelectedPermissions(initialPermissions);
  };

  const hasChanges = JSON.stringify(selectedPermissions.sort()) !== JSON.stringify(initialPermissions.sort());

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        width: "100%",
        gap: 2,
      }}
    >
      {/* Role Selection */}
      <Card>
        <CardContent>
          <FormControl fullWidth>
            <InputLabel id="role-select-label">{i18n.t("role-mapping-tab.select-role.label")}</InputLabel>
            <Select
              labelId="role-select-label"
              value={selectedRoleId}
              label={i18n.t("role-mapping-tab.select-role.label")}
              onChange={(e) => handleRoleChange(e.target.value)}
              disabled={getRoles.isFetching}
            >
              {getRoles.data?.roles?.map((role) => (
                <MenuItem key={role.roleId} value={role.roleId || ""}>
                  {role.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </CardContent>
      </Card>

      {/* Permissions Checkboxes */}
      {selectedRoleId && (
        <Card sx={{ flexGrow: 1, overflow: "auto" }}>
          <CardContent>
            <Stack spacing={3}>
              <Typography variant="h6">{i18n.t("role-mapping-tab.permissions.title")}</Typography>

              {getRolePermissions.isFetching ? (
                <Typography>{i18n.t("role-mapping-tab.permissions.loading")}</Typography>
              ) : availablePermissions.length === 0 ? (
                <Typography color="text.secondary">No permissions available for this role. Check console for API response.</Typography>
              ) : (
                <FormGroup>
                  {availablePermissions.map((permissionName) => {
                    const isDefault = defaultPermissions.includes(permissionName);
                    const isAppManageSelected = selectedPermissions.includes("app.manage");
                    const isAppManage = permissionName === "app.manage";

                    // Disable if:
                    // 1. It's a default permission, OR
                    // 2. app.manage is selected and this is not app.manage itself
                    const isDisabled = isDefault || (isAppManageSelected && !isAppManage);

                    return (
                      <FormControlLabel
                        key={permissionName}
                        control={<Checkbox checked={selectedPermissions.includes(permissionName)} onChange={() => handlePermissionToggle(permissionName)} disabled={isDisabled} />}
                        label={
                          <Box>
                            <Typography variant="body1" color={isDisabled ? "text.secondary" : "text.primary"}>
                              {permissionName}
                              {isDefault && (
                                <Typography component="span" variant="caption" sx={{ ml: 1, fontStyle: "italic" }}>
                                  (Default)
                                </Typography>
                              )}
                            </Typography>
                          </Box>
                        }
                      />
                    );
                  })}
                </FormGroup>
              )}

              {/* Action Buttons */}
              <Stack direction="row" spacing={2} justifyContent="flex-end">
                <Button variant="outlined" onClick={handleCancel} disabled={!hasChanges || updateRolePermissions.isPending}>
                  {i18n.t("role-mapping-tab.button.cancel")}
                </Button>
                <Button variant="contained" onClick={handleSave} disabled={!hasChanges || updateRolePermissions.isPending} loading={updateRolePermissions.isPending}>
                  {i18n.t("role-mapping-tab.button.save")}
                </Button>
              </Stack>
            </Stack>
          </CardContent>
        </Card>
      )}

      {/* Snackbar for notifications */}
      <Snackbar open={openSnackbar} autoHideDuration={3000} onClose={() => setOpenSnackbar(false)} anchorOrigin={{ vertical: "top", horizontal: "right" }}>
        <Alert onClose={() => setOpenSnackbar(false)} severity="success" sx={{ width: "100%" }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
});

export default RoleMappingGrid;
